<template>
  <div
    class="card guide"
    :class="{
      'guide-danger': state === 'error',
      'guide-secondary': state === 'loading',
      'guide-info': ['assigning', 'empty', 'autoAssigned'].includes(state),
      'guide-warning': ['notAssigned', 'outOfStock', 'problematic', 'pricesOutdated', 'unavailable'].includes(state),
      'guide-success': state === 'ready',
    }"
  >
    <div class="card-body d-flex flex-column flex-md-row align-items-md-center gap-3">
      <template v-if="state === 'error'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-circle-exclamation" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.error.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.error.body')" />
        </div>
      </template>

      <template v-else-if="state === 'loading'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="spinner-border spinner-border-sm" />
        </div>
        <div class="flex-grow-1">
          <template v-if="foregroundJob?.name === 'board_processing'">
            <h5 class="card-title h6 mb-1">
              {{ $t('BomGuide.boardProcessing.title') }}
            </h5>
            <p class="guide-description mb-0" v-html="$t('BomGuide.boardProcessing.body')" />
          </template>
          <template v-else-if="foregroundJob?.name === 'auto_assigning'">
            <h5 class="card-title h6 mb-1">
              {{ $t('BomGuide.autoAssigning.title', { progress: foregroundJob.message || '' }) }}
            </h5>
            <p class="guide-description mb-0" v-html="$t('BomGuide.autoAssigning.body')" />
          </template>
          <template v-else-if="foregroundJob?.name === 'bom_updating'">
            <h5 class="card-title h6 mb-1">
              {{ $t('BomGuide.bomUpdating.title', { progress: foregroundJob.message || '' }) }}
            </h5>
            <p class="guide-description mb-0" v-html="$t('BomGuide.bomUpdating.body')" />
          </template>
          <template v-else-if="foregroundJob?.name === 'optimizing_process'">
            <h5 class="card-title h6 mb-1">
              {{ $t('BomGuide.optimizing.title', { progress: foregroundJob.message || '' }) }}
            </h5>
            <p class="guide-description mb-0" v-html="$t('BomGuide.optimizing.body')" />
          </template>
          <template v-else>
            <h5 class="card-title h6 mb-1">
              {{ $t('BomGuide.loading.title') }}
            </h5>
            <p class="guide-description mb-0" v-html="$t('BomGuide.loading.body')" />
          </template>

          <div
            class="progress mt-2"
            role="progressbar"
            :aria-valuenow="progress ?? undefined"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="progress-bar text-bg-success progress-bar-striped progress-bar-animated"
              :style="{ width: `${progress ?? 0}%` }"
            />
          </div>
        </div>
      </template>

      <template v-else-if="state === 'assigning'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-magnifying-glass" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.assigning.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.assigning.body')" />
        </div>
      </template>

      <template v-else-if="state === 'empty'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-microchip" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.empty.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.empty.body')" />
        </div>
      </template>

      <template v-else-if="state === 'notAssigned'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-triangle-exclamation" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.notAssigned.title', partsByStatus.NOT_ASSIGNED.length) }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.notAssigned.body')" />
          <MpnPropertyKeySelect />
        </div>
        <div class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <button
            v-if="bomStore.pricesOutdated"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="bomStore.loading"
            @click="bomStore.refreshPrices()"
          >
            {{ $t('BomGuide.calculatePrices') }}
          </button>
          <button
            type="button"
            class="btn btn-aisler-gradient"
            :disabled="bomStore.loading"
            @click="assignStore.startWizard(groupSimilarParts(partsByStatus.NOT_ASSIGNED))"
          >
            {{ $t('BomGuide.assignParts', partsByStatus.NOT_ASSIGNED.length) }}
          </button>
        </div>
      </template>

      <template v-else-if="state === 'autoAssigned'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-wand-magic-sparkles" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.autoAssigned.title', partsByStatus.AUTO_ASSIGNED.length) }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.autoAssigned.body')" />
          <MpnPropertyKeySelect />
        </div>
        <div class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <button
            v-if="bomStore.pricesOutdated"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="bomStore.loading"
            @click="bomStore.refreshPrices()"
          >
            {{ $t('BomGuide.calculatePrices') }}
          </button>
          <button
            type="button"
            class="btn btn-aisler-gradient"
            :disabled="bomStore.loading"
            @click="bomStore.focusStatus('AUTO_ASSIGNED')"
          >
            {{ $t('BomGuide.reviewParts', partsByStatus.AUTO_ASSIGNED.length) }}
          </button>
        </div>
      </template>

      <template v-else-if="state === 'outOfStock'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-box-open" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.outOfStock.title', partsByStatus.OUT_OF_STOCK.length) }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.outOfStock.body')" />
        </div>
        <div class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <button
            v-if="bomStore.pricesOutdated"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="bomStore.loading"
            @click="bomStore.refreshPrices()"
          >
            {{ $t('BomGuide.calculatePrices') }}
          </button>
          <button
            type="button"
            class="btn btn-aisler-gradient"
            :disabled="bomStore.loading"
            @click="bomStore.focusStatus('OUT_OF_STOCK')"
          >
            {{ $t('BomGuide.reviewParts', partsByStatus.OUT_OF_STOCK.length) }}
          </button>
        </div>
      </template>

      <template v-else-if="state === 'problematic'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-ruler-combined" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.problematic.title', partsByStatus.PROBLEMATIC.length) }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.problematic.body')" />
        </div>
        <div class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <button
            v-if="bomStore.pricesOutdated"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="bomStore.loading"
            @click="bomStore.refreshPrices()"
          >
            {{ $t('BomGuide.calculatePrices') }}
          </button>
          <button
            type="button"
            class="btn btn-aisler-gradient"
            :disabled="bomStore.loading"
            @click="bomStore.focusStatus('PROBLEMATIC')"
          >
            {{ $t('BomGuide.reviewParts', partsByStatus.PROBLEMATIC.length) }}
          </button>
        </div>
      </template>

      <template v-else-if="state === 'pricesOutdated'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-calculator" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.pricesOutdated.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.pricesOutdated.body')" />
        </div>
        <div class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <button
            type="button"
            class="btn btn-aisler-gradient"
            :disabled="bomStore.loading"
            @click="bomStore.refreshPrices()"
          >
            {{ $t('BomGuide.calculatePrices') }}
          </button>
        </div>
      </template>

      <template v-else-if="state === 'unavailable'">
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-box-open" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.unavailable.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.unavailable.body')" />
        </div>
      </template>

      <template v-else>
        <div class="guide-icon d-none d-md-flex align-items-center justify-content-center flex-shrink-0">
          <i class="fas fa-circle-check" />
        </div>
        <div class="flex-grow-1">
          <h5 class="card-title h6 mb-1">
            {{ $t('BomGuide.ready.title') }}
          </h5>
          <p class="guide-description mb-0" v-html="$t('BomGuide.ready.body')" />
        </div>
        <div v-if="bomStore.checkoutPath" class="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
          <a
            class="btn btn-aisler-gradient"
            :class="{ disabled: bomStore.loading }"
            :href="bomStore.checkoutPath"
          >
            {{ $t('BomGuide.checkout') }}
          </a>
        </div>
      </template>
    </div>

    <div v-if="optimizingJob && !['loading', 'error'].includes(state)" class="card-body pt-0">
      <p class="guide-description mb-1">
        {{ $t('BomGuide.optimizing.title', { progress: optimizingJob.message || '' }) }}
      </p>
      <div
        class="progress"
        role="progressbar"
        :aria-valuenow="progress ?? undefined"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div
          class="progress-bar text-bg-success progress-bar-striped progress-bar-animated"
          :style="{ width: `${progress ?? 0}%` }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import messages from 'locales/BomGuide';
import Part from 'store/bom/Part';
import { useBomStore, useAssignStore } from 'store/bom';
import { groupSimilarParts } from './lib/TableEntry';
import MpnPropertyKeySelect from './MpnPropertyKeySelect.vue';

export default {
  name: 'BomGuide',
  components: { MpnPropertyKeySelect },
  setup() {
    const bomStore = useBomStore();
    const assignStore = useAssignStore();
    return { bomStore, assignStore, groupSimilarParts };
  },
  computed: {
    partsByStatus() {
      return _.groupBy(this.bomStore.parts, (part) => part.getStatus());
    },
    initialLoading() {
      return this.bomStore.loading && !this.bomStore.parts.length > 0;
    },
    runningJobs() {
      return this.bomStore.jobStatus.filter((job) => job.isProcessing);
    },
    // The first running job in this order is shown while the store reports foreground jobs
    foregroundJob() {
      if (this.bomStore.error || this.initialLoading || !this.bomStore.areForegroundJobsRunning) return null;

      return ['board_processing', 'auto_assigning', 'bom_updating', 'optimizing_process']
        .map((name) => this.runningJobs.find((job) => job.name === name))
        .find(Boolean) || null;
    },
    optimizingJob() {
      return this.runningJobs.find((job) => job.name === 'optimizing_process') || null;
    },
    progress() {
      const percentage = parseFloat((this.foregroundJob || this.optimizingJob)?.pctComplete);
      return Number.isFinite(percentage) ? _.clamp(percentage, 0, 100) : null;
    },
    // Unresolved part statuses come first, in this order
    state() {
      if (this.bomStore.error) return 'error';
      if (this.initialLoading || this.bomStore.areForegroundJobsRunning) return 'loading';
      if (this.assignStore.active) return 'assigning';
      if (!this.bomStore.parts.length > 0) return 'empty';
      if (this.partsByStatus[Part.NOT_ASSIGNED]) return 'notAssigned';
      if (this.partsByStatus[Part.AUTO_ASSIGNED]) return 'autoAssigned';
      if (this.partsByStatus[Part.OUT_OF_STOCK]) return 'outOfStock';
      if (this.partsByStatus[Part.PROBLEMATIC]) return 'problematic';
      if (this.bomStore.pricesOutdated) return 'pricesOutdated';
      if (this.bomStore.quantityPrice?.availability === 'unavailable') return 'unavailable';
      return 'ready';
    },
  },
  i18n: { messages },
};
</script>

<style lang="scss" scoped>
// Deep, so child components like MpnPropertyKeySelect share the style
:deep(.guide-description) {
  color: rgba(var(--bs-body-color-rgb), 0.8);
}

@each $color in warning, info, success, danger, secondary {
  .guide-#{$color} {
    background-color: var(--bs-#{$color}-bg-subtle);
    border-color: var(--bs-#{$color}-border-subtle);

    .guide-icon {
      color: var(--bs-#{$color}-text-emphasis);
    }
  }
}

.guide-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  font-size: 1.1rem;
  background-color: var(--bs-body-bg);
}

</style>
